/**
 * 위치 키프레임 — "이 시점(아이템 로컬 프레임)에 기준 위치 대비 (dx, dy)px".
 *
 * 아이템의 `positionKeyframes` 필드(CanHaveAnimation)에 저장되는 1급 데이터다.
 * 렌더는 calculate-animation 이 현재 프레임의 오프셋(getPositionOffsetAt, 구간 밖
 * clamp·구간 내 선형 보간)을 px translate 로 합성한다 — 애니 프리셋과 독립.
 */
export interface PositionKeyframe {
  /** 아이템 로컬 프레임 */
  frame: number;
  /** 기준 위치(item.left/top) 대비 px */
  dx: number;
  dy: number;
}

/** 같은 프레임(반올림 기준)이면 교체, 아니면 삽입한 새 목록(프레임 오름차순). */
export const upsertPositionKeyframe = (
  keyframes: PositionKeyframe[],
  keyframe: PositionKeyframe,
): PositionKeyframe[] => {
  const frame = Math.round(keyframe.frame);
  const next = keyframes.filter((k) => Math.round(k.frame) !== frame);
  next.push({ ...keyframe, frame });
  return next.sort((a, b) => a.frame - b.frame);
};

/** 키프레임 곡선의 특정 프레임 오프셋(px) — 구간 밖은 clamp, 구간 내 선형 보간. */
export const getPositionOffsetAt = (
  keyframes: PositionKeyframe[],
  frame: number,
): { dx: number; dy: number } => {
  if (keyframes.length === 0) return { dx: 0, dy: 0 };
  const sorted = [...keyframes].sort((a, b) => a.frame - b.frame);
  const first = sorted[0] as PositionKeyframe;
  const last = sorted[sorted.length - 1] as PositionKeyframe;
  if (frame <= first.frame) return { dx: first.dx, dy: first.dy };
  if (frame >= last.frame) return { dx: last.dx, dy: last.dy };
  for (let i = 0; i < sorted.length - 1; i++) {
    const a = sorted[i] as PositionKeyframe;
    const b = sorted[i + 1] as PositionKeyframe;
    if (frame >= a.frame && frame <= b.frame) {
      const t =
        b.frame === a.frame ? 0 : (frame - a.frame) / (b.frame - a.frame);
      return { dx: a.dx + (b.dx - a.dx) * t, dy: a.dy + (b.dy - a.dy) * t };
    }
  }
  return { dx: last.dx, dy: last.dy };
};

/** 반올림 프레임 기준으로 그 프레임에 키프레임이 있는지. */
export const hasKeyframeAt = (
  keyframes: PositionKeyframe[],
  frame: number,
): boolean => {
  const target = Math.round(frame);
  return keyframes.some((k) => Math.round(k.frame) === target);
};

/** 반올림 프레임 기준으로 그 프레임의 키프레임을 제거한 새 목록. */
export const removeKeyframeAt = (
  keyframes: PositionKeyframe[],
  frame: number,
): PositionKeyframe[] => {
  const target = Math.round(frame);
  return keyframes.filter((k) => Math.round(k.frame) !== target);
};

/** 현재 프레임(반올림)보다 앞선 가장 가까운 키프레임. 없으면 undefined. */
export const findPrevKeyframe = (
  keyframes: PositionKeyframe[],
  frame: number,
): PositionKeyframe | undefined => {
  const target = Math.round(frame);
  const before = keyframes
    .filter((k) => Math.round(k.frame) < target)
    .sort((a, b) => a.frame - b.frame);
  return before[before.length - 1];
};

/** 현재 프레임(반올림)보다 뒤인 가장 가까운 키프레임. 없으면 undefined. */
export const findNextKeyframe = (
  keyframes: PositionKeyframe[],
  frame: number,
): PositionKeyframe | undefined => {
  const target = Math.round(frame);
  const after = keyframes
    .filter((k) => Math.round(k.frame) > target)
    .sort((a, b) => a.frame - b.frame);
  return after[0];
};
